<template>
  <el-container class="screenHeight">
    <el-container>
      <el-main>
        <CustomMeshLoader
          ref="meshLoader"
          v-model:uploadProgress="activeUploadProgress"
          :can-select="true"
          :can-take-snapshot="true"
          :can-toggle-opacity="true"
          :directory-path="authentication.user?.username"
          :is-product-config="isProductConfig"
          :workflow-model="workflowModel"
          :fullscreen-loading="fullscreenLoading"
          :meshEngravings="meshEngravings"
          :meshTextures="meshTextures"
          :meshEngravingsLoading="meshEngravingsLoading"
          :meshTexturesLoading="meshTexturesLoading"
          :domainPackages="domainPackages"
          @product-type-id-updated="handleProductTypeIdUpdated"
        />
      </el-main>
    </el-container>
  </el-container>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-facing-decorator';
import { ElMessageBox } from 'element-plus';

import CustomMeshLoader, { MeshSizesType, MeshVectorType } from '@/components/three/scenes/CustomMeshLoader.vue';
import { FileTypeImage, FileTypeVideo } from '@/types/enum/upload';
import { UploadProgress } from '@/types/enum/workflow';
import * as LayoutUtility from '@/utils/layout';
import * as ModelService from '@/services/api/modelService';
import * as ProductService from '@/services/api/productService';
import * as FormBuilderService from '@/services/api/formBuilderService';
import Workflow from '@/components/workflow/Workflow.vue';
import { WorkflowModel } from '@/types/ui/WorkflowModel';
import Auth from '@/services/api/auth';
import {
  saveFileMetadataToAzureTables,
  TableField,
  blobUrlToFile,
  extractExtensionFromBase64,
  generateRandomFilename,
  uploadToAzure,
} from '@/utils/file';
import { MeshTreeData } from '@/types/ui/MeshTreeData';
import { useRouter } from 'vue-router';
import * as TemplateService from '@/services/api/templateService';
import { SupportedValues, SupportedValuesTextures } from '@/types/api/Utility/SupportedValues';
import { TextureType } from '@/services/api/modelService' 
import basePricesData from '@/data/basePrice.json';
import { BasePriceDto } from '@/types/api/Model/ProductUpsertRequest';
import { useFeatureFlagStore } from '@/stores/featureFlagStore';
import { useUserStore } from '@/stores/userStore';
import { getDomainByUrlSuffix } from '@/services/api/userService';
import { defaultUrlSuffix } from '@/types/constants/url-suffix'
import { Domain } from '@/types/api/Business/Domain';
import { Package } from '@/types/api/Business/Package';
import { ComponentIdsType } from '../../services/api/modelService';

type PackageWithIsPublic = Package & {
  isPublic?: boolean;
};

export type DomainPackagesOptions = {
  label: string;
  value: number;
  disabled?: boolean;
}

@Component({
  components: {
    CustomMeshLoader,
    Workflow,
  },
})
/* eslint-disable @typescript-eslint/no-explicit-any*/

export default class TemplateUpload extends Vue {
  @Prop() templateId!: number | null;

  router = useRouter();

  workflowModel: WorkflowModel<UploadProgress> = new WorkflowModel<UploadProgress>('upload-progress', UploadProgress);
  fullscreenLoading = false;
  authentication = Auth.getInstance();
  abortController = new AbortController();

  isProductConfig = false;
  meshEngravings: SupportedValues[] = [];
  meshTextures: SupportedValuesTextures[] = [];
  meshEngravingsLoading = false;
  meshTexturesLoading = false;
  featureFlagStore =  useFeatureFlagStore();
  domainPackages: DomainPackagesOptions[] = [];

  get activeUploadProgress(): UploadProgress {
    if (this.workflowModel.active < this.workflowModel.stepCount) {
      LayoutUtility.refresh();
      return Object.values(UploadProgress)[this.workflowModel.active];
    }
    return UploadProgress.Pivot;
  }

  set activeUploadProgress(value: UploadProgress) {
    this.workflowModel.setStep(Object.values(UploadProgress).indexOf(value));
  }

  mounted(): void {
    if(!this.featureFlagStore.isTemplateUploadEnabled()){
      this.router.back();
      return 
    }
    this.authentication.handleAuthentication().then(() => {
      //get user information
      // console.log('this.authentication', this.authentication);
    });

    const getUserDomain = async () => {
      const userStore = useUserStore();
      if (userStore && userStore.domain) {
        
        const urlSuffix = userStore.domain.urlSuffix;
        const domain = await getDomainByUrlSuffix(urlSuffix);
        const isSupplierAdmin = urlSuffix === defaultUrlSuffix

        if (domain && domain.packages?.length > 0) {
          this.domainPackages = domain.packages.map((item: PackageWithIsPublic) => {
            const isPublic = item.isPublic;
            const packageType = isPublic ? 'Public' : 'Private';
            const disabled = !isSupplierAdmin && isPublic;
            return ({
              label: `${item.description} - (${packageType})`,
              value: item.packageId,
              disabled,
            })
          })
        }

      }
    }

    getUserDomain()
  }

  async prepareFormBuilderPayloads(configFields: any[]) {
    const newFormFields = await Promise.all(
      configFields.map(async (c) => {
        let rootImage = c.image;
        let rootOptions = c.options;
        if (c.imageType === 'file') {
          try {
            const ext = extractExtensionFromBase64(c.image);
            const filename = generateRandomFilename(ext);
            const file = await blobUrlToFile(c.image, filename);
            const fileAzureUrl = await uploadToAzure({
              file: file,
              direcatoryPath: '',
              containerName: 'formbuilder',
              type: 'formbuilder',
            });
            rootImage = fileAzureUrl;
          } catch (error) {
            console.log('error main image', error);
          }
        }

        if (c.options) {
          const newOptions = await Promise.all(
            c.options.map(async (option: { image: any; imageType: string; }) => {
              let optionImage = option.image;
              if (option.imageType === 'file') {
                try {
                  const ext = extractExtensionFromBase64(c.image);
                  const filename = generateRandomFilename(ext);
                  const file = await blobUrlToFile(c.image, filename);
                  const fileAzureUrl = await uploadToAzure({
                    file: file,
                    direcatoryPath: '',
                    containerName: 'formbuilder',
                    type: 'formbuilder',
                  });
                  optionImage = fileAzureUrl;
                } catch (error) {
                  console.log('error option', error);
                }
              }

              return {
                ...option,
                image: optionImage,
              };
            })
          );

          rootOptions = newOptions;
        }

        return {
          ...c,
          image: rootImage,
          ...(rootOptions
            ? {
                options: rootOptions,
              }
            : {}),
        };
      })
    );
    return JSON.stringify(newFormFields || []);
  }


  constructEngravingOrTexture(files: any[]){
    const newFiles = files.map((f, i) => ({
          id: i + 1,
          name: f.thumbnail ?? '',
          content: f.thumbnailUrl ?? '',
          type: f.thumbnailFiletype ?? '',
        }));

    return newFiles
  }

  async uploadEngraving({ loader, productId }: { loader: CustomMeshLoader; productId: number }) {
    try {
      const payload = {
        productId,
        engravingImageFilesLR: this.constructEngravingOrTexture(loader.selectedEngravingImagesLR),
        engravingImageFilesHR: this.constructEngravingOrTexture(loader.selectedEngravingImagesHR),
        abortController: this.abortController,
      };

      await ModelService.addEngravingToModel(payload);
      console.log('CREATE ENGRAVING', true);
    } catch (error) {
      console.error('ERROR CREATE ENGRAVING', error);
    }
  }

  async uploadTexture({ loader, productId }: { loader: CustomMeshLoader; productId: number }) {
    try {
      const payload = {
        productId,
        textureImageFilesLR: this.constructEngravingOrTexture(loader.selectedTextureImagesLR),
        textureImageFilesHR: this.constructEngravingOrTexture(loader.selectedTextureImagesHR),
        abortController: this.abortController,
      };

      await ModelService.addTextureToModel(payload);
      console.log('CREATE TEXTURE', true);
    } catch (error) {
      console.error('ERROR CREATE TEXTURE', error);
    }
  }

  async uploadColor({ loader, productId }:{
    loader: CustomMeshLoader,
    productId: number
  }) {
    try {

      const payload = {
        productId,
        selectedColors: loader.selectedColors,
        title: loader.loadingData.title,
        abortController: this.abortController,
      };

      await ModelService.applyColorDesignToExistingProduct(payload);
      console.log('CREATE COLOR', true);
    } catch (error) {
      console.error('ERROR CREATE COLOR', error);
    }
  }

  async ApplyTextureDesignToExistingProduct({ loader, productId, textureType }: { loader: CustomMeshLoader; productId: number, textureType: TextureType }): Promise<void> {
    if (!productId || !loader || !textureType) {
      console.warn('productId, loader or textureType not found!');
      return;
    }

    let selectedImagesHR, textureOptionsIds;

    if (textureType === TextureType.ENGRAVING) {
      selectedImagesHR = loader.selectedEngravingImagesHR;
      textureOptionsIds = loader.selectedEngraving;
    } else if (textureType === TextureType.PRINT) {
      selectedImagesHR = loader.selectedTextureImagesHR;
      textureOptionsIds = loader.selectedTextures;
    } else {
      console.warn('Unknown textureType:', textureType);
      return;
    }

    const defaultDesign = null;
    const newFiles = selectedImagesHR.map((item) => ({
      hrFile: {
        name: item.thumbnail?.split('.')?.[0] ?? '',
        type: item.thumbnailFiletype ?? FileTypeImage.NONE,
        content: item.thumbnailUrl ?? '',
      },
      lrFile: null
    }));


    await ModelService.AddEngravingOrTextureDesign({
      productId,
      textureType,
      textureOptionsIds,
      defaultDesign,
      newFiles,
      abortController: this.abortController,
    });
  }

  async ApplySizeToExistingProduct({loader, productId, componentIds}: { loader: CustomMeshLoader; productId: number, componentIds: ComponentIdsType[] }): Promise<void> {
    const { meshSizes } = loader.loadingData;
    const sizeInput = componentIds.map(component => {
      const { componentId } = component;
      const mesh = meshSizes.find((item) => item.uuid === component.uuid);
      if (mesh) {
        const {width, height, depth} = mesh.vector;
        return ({
          componentId,
          width,
          height,
          depth
        })
      } 

      return undefined;
    })
    .filter((item): item is ModelService.MeshVectorSizeType => item !== undefined);

    await ModelService.applySizeToExistingProduct({
      productId,
      sizeInput,
      abortController: this.abortController,
    });
  }

  async initialCreateProductParams(loader: CustomMeshLoader, configFields: string | null): Promise<ModelService.UploadNewProductType> {
    const { packageIds, description, productTypeId, category, title } = loader.loadingData;
    const name = configFields ? `${title} phase1`: title;
    const bodyPart = loader.loadingData.bodyPart.name !== 'Test' ? loader.loadingData.bodyPart : null
    const productType = productTypeId ? { id: productTypeId} : null;
    const thumbnail = {
      name: loader.selectedInput.thumbnail ?? '',
      content: loader.selectedInput.thumbnailUrl ?? '',
      type: loader.selectedInput.thumbnailFiletype ?? '',
    };
    const basePrices: BasePriceDto[] = loader.loadingData.basePrices.length > 0 ? 
      loader.loadingData.basePrices : 
      basePricesData as BasePriceDto[];
    const treeData = loader.selectedInput.treeData;
    const productTypeAttributeId = loader.loadingData.productTypeAttributeId;

    console.log('BasePrices: ', basePrices);

    return ({
      packageIds,
      name,
      description,
      productType,
      bodyPart,
      thumbnail,
      basePrices,
      treeData,
      category,
      productTypeAttributeId
    })
  }

  async handleSubmitNewProduct(
    loader: CustomMeshLoader, 
    params: ModelService.UploadNewProductType
  ): Promise<{
    modelId: number, 
    productId: number,
    componentIds?: ComponentIdsType[]
  }> {

    if (!loader || !params) {
      console.warn('loader or params data is null!')
    }

    const getMeshdata = loader.getResult();

    if (!getMeshdata) {
      console.warn('mesh and bone is null!')
    }

    const response = await ModelService.uploadNewProduct(params)
  
    return response;
  }

  @Watch('workflowModel.finished', { immediate: true })
  async onStepFinished(): Promise<void> {
    if(!this.workflowModel.finished) return
    
    this.fullscreenLoading = true;
    let loader = this.$refs.meshLoader as CustomMeshLoader;
    const configFields = localStorage.getItem('fields');
    const params = await this.initialCreateProductParams(loader, configFields);
    let createdProductId
    try {
      const submitResult = await this.handleSubmitNewProduct(loader, params)
      
      // if templateId is specified, productId is always null.
      if (this.templateId) {
        this.router.go(-1);
        return
      }

      if (!submitResult) return
      const { productId, componentIds } = submitResult;
      createdProductId = productId
      if(loader.selectedEngravingImagesHR.length > 0 || loader.selectedEngraving.length > 0)
        await this.ApplyTextureDesignToExistingProduct({
          loader,
          productId: productId,
          textureType: TextureType.ENGRAVING // for engraving use ‘ENGRAVING’ and texture use 'PRINT'
        })
        
      if(loader.selectedTextureImagesHR.length > 0 || loader.selectedTextures.length > 0)
        await this.ApplyTextureDesignToExistingProduct({
          loader,
          productId: productId,
          textureType: TextureType.PRINT
        });

      if(loader.selectedColors.length > 0)
        await this.uploadColor({
          loader,
          productId: productId,
        });
      
      if(loader.loadingData.meshSizes.length > 0 && componentIds && componentIds.length > 0) 
        await this.ApplySizeToExistingProduct({
          loader,
          productId,
          componentIds
        })

      this.fullscreenLoading = false;
      // this.router.push('/templates');
      await this.createFormBuilder({configFields, productId, templateId:this.templateId})

      // Store video metadata on Azure Tables Storage ...
      await this.handleStoreVideoMetadataToAzure(
        productId.toString(), //convert number to string
        loader.selectedInput
      );
        
      this.router.push('/templates?uploaded=true');
    } catch (error) {
      console.error('[Error Upload Product]:',error)
      if (!createdProductId)
        return
      await ProductService.DeleteProductById(createdProductId)
    }
  }

  async createFormBuilder({configFields, productId, templateId}: {configFields: any, productId: number, templateId: number | null}) {
    // Disable this gql request since the feature is disabled at the moment
    const disableFormBuilder = true
    if(disableFormBuilder) return
    try {
      const newConfigFields = await this.prepareFormBuilderPayloads(JSON.parse(configFields ?? '[]'));
      await FormBuilderService.createProductFormBuilder({
        productId: productId,
        formFields: newConfigFields || '[]',
      })
      localStorage.removeItem('fields');
      const meshLoaderRef = this.$refs.meshLoader as CustomMeshLoader;
      if (meshLoaderRef) meshLoaderRef.resetFormBuilderState();
      this.router.replace(`/template-upload?templateId=${templateId}`);
    } catch (error) {
      console.error('[Error Form Builder]', error);
    }
    
  }

  async handleStoreVideoMetadataToAzure(productId: string, payload: MeshTreeData): Promise<void> {
    if (!productId.length || !payload) return;

    // destructor video and video thumbnail metadata
    const { video, videoFiletype, videoUrl, videoThumbnail, videoThumbnailFiletype, videoThumbnailUrl } = payload;

    const videoData: TableField[] = [];

    if (payload.videoUrl?.length) {
      videoData.push({
        productId,
        filename: video || '',
        fileType: videoFiletype || FileTypeVideo.WEBM,
        fileUrl: videoUrl || '',
      });
    }

    if (payload.videoThumbnailUrl?.length) {
      videoData.push({
        productId,
        filename: videoThumbnail || '',
        fileType: videoThumbnailFiletype || FileTypeImage.PNG,
        fileUrl: videoThumbnailUrl || '',
      });
    }

    if (!videoData.length) return;

    await saveFileMetadataToAzureTables(videoData);
  }

  @Watch('workflowModel.active', { immediate: true })
  onWorkflowModel(newValue: number, oldValue: number): void {
    const fields = localStorage.getItem('fields');
    if (oldValue === 0 && newValue === 1 && fields) this.showConfirmUseProductConfig();
  }

  showConfirmUseProductConfig(): void {
    const meshLoaderRef = this.$refs.meshLoader as CustomMeshLoader;
    // const confirmYes = makeWordToCapitalized((this as any).$t('components.mesh-loader.yes'), 'yes')
    // const confirmNo = makeWordToCapitalized((this as any).$t('components.mesh-loader.yes'), 'no')

    ElMessageBox.confirm((this as any).$t('components.mesh-loader.productConfigConfirmMessage'), 'info', {
      confirmButtonText: (this as any).$t('components.mesh-loader.yes'),
      cancelButtonText: (this as any).$t('components.mesh-loader.no'),
      type: 'warning',
      closeOnClickModal: false,
      showClose: false,
    })
      .then(() => {
        // if confirm next to use product confirm ...
        this.isProductConfig = true;
        if (meshLoaderRef) meshLoaderRef.updateProductConfigButtonText(true);
      })
      .catch(() => {
        localStorage.removeItem('fields');
        this.isProductConfig = false;
        if (meshLoaderRef) meshLoaderRef.updateProductConfigButtonText(false);
      });
  }

  handleBack(): void {
    this.router.push({
      name: 'template-workflow',
      query: { templateId: this.templateId }
    });
  }

  async loadEngravingByProductTypeFromDB(productTypeId: number, textureType: string, shouldRefetch: boolean) {
    // Start loading 
    this.meshEngravingsLoading = true;

    // Call engraving endpoint 
    const engravingByProductType = await TemplateService.getEngravingByProductTypeData(productTypeId, textureType, shouldRefetch);
    
    if (!engravingByProductType) return; // Validate result 

    // Mapping the data
    this.meshEngravings = engravingByProductType.map(item => ({
      fileId: item.fileId,
      fileTextureId: item.textureOptionId,
      thumbnail: item.thumbnail
    }))

    // End loading 
    this.meshEngravingsLoading = false;
  }

  async loadTextureByProductTypeFromDB(productTypeId: number, textureType: string, shouldRefetch: boolean) {
    this.meshTexturesLoading = true;
    const textureByProductType = await TemplateService.getEngravingByProductTypeData(productTypeId, textureType, shouldRefetch);
    if (!textureByProductType) {
      this.meshTexturesLoading = false;
      return;
    }
    this.meshTextures = textureByProductType.map(item => {
      return {
        fileId: item.fileId,
        fileTextureId: item.textureOptionId,
        fileName: item.textureName,
        fileContent: item.file?.content,
        thumbnail: item.thumbnail,
      };
    });
    this.meshTexturesLoading = false;
  }

  async loadEngravingFromDB() {
    const componentIdTemprary = 136;
    const engraving = await TemplateService.getEmbossingData(componentIdTemprary, true);
    if(!engraving) return;
    const { supportedValues } = engraving;
    const reordered = [
      supportedValues[1],               // move row with index 2 to 1
      ...supportedValues.slice(0, 1),   // move row with index 1 to 2
      ...supportedValues.slice(2),      // the rest remains
    ];
    this.meshEngravings = reordered.map(item => ({
      fileId: item.file.fileId,
      fileTextureId: item.fileTextureId,
      thumbnail: item.thumbnail
    }))
  }

  async loadTexturesFromDB() {
    const componentIdTemprary = 3079;
    const texture = await TemplateService.getTextureData(componentIdTemprary, true);

    if (!texture?.supportedValues || !Array.isArray(texture.supportedValues)) {
      console.warn('loadTexturesFromDB: supportedValues invalid', texture);
      return;
    }

    const { supportedValues } = texture;

    this.meshTextures = supportedValues
      .filter(item => {
        const hasFile = item.file && typeof item.file === 'object';
        if (!hasFile) {
          console.warn('Skipping unsupportedValue without file:', item);
        }
        return hasFile;
      })
      .map(item => {
        const { file, fileTextureId, thumbnail } = item;
        return {
          fileId: file.fileId ?? null,
          fileTextureId: fileTextureId ?? null,
          fileName: file.name ?? '',
          fileContent: file.content ?? '',
          thumbnail: thumbnail ? thumbnail : null,
        };
      });
  }

  handleProductTypeIdUpdated(productTypeId: number | null): void {
    if (!productTypeId) return;
    this.loadEngravingByProductTypeFromDB(productTypeId, "ENGRAVING", true)
    this.loadTextureByProductTypeFromDB(productTypeId, "PRINT", true)
  }
}
</script>

<style lang="scss" scoped>
.el-main :deep() {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: auto;
}

.level:not(:last-child) {
  margin-bottom: unset;
}

.screenHeight {
  display: flex;
  flex-direction: column;
  height: 93vh;
}

.workflow {
  z-index: 1;
}

.el-footer {
  display: flex;
  justify-content: center;
  align-items: center;
}

:deep(.el-steps) {
  width: 100%;
}
</style>
